import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import { detectLanguage } from '../../utils/detectLanguage';
import { switchLanguage } from '../../utils/switchLanguage';

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const location = useLocation();
  const navigate = useNavigate();

  // Detect current language from URL using safe utility
  useEffect(() => {
    const detectedLang = detectLanguage(location?.pathname);
    setCurrentLanguage(detectedLang);
  }, [location]);

  // Check if current page is a calculator page
  const isCalculatorPage = () => {
    const path = location?.pathname;
    const normalizedPath = path?.replace(/^\/(es|pt|fr|de|it|nl)/, '') || '/';
    return normalizedPath?.includes('/debt-freedom-calculator') || 
           normalizedPath?.includes('/money-duration-calculator');
  };

  const navigationItems = [
    {
      label: 'Money Duration Calculator',
      path: '/money-duration-calculator',
      icon: 'Calculator'
    },
    {
      label: 'Debt Freedom Calculator',
      path: '/debt-freedom-calculator',
      icon: 'TrendingUp'
    }
  ];

  const languages = [
    { code: 'en', label: 'English', flag: '🇺🇸' },
    { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
    { code: 'es', label: 'Español', flag: '🇪🇸' },
    { code: 'fr', label: 'Français', flag: '🇫🇷' },
    { code: 'pt', label: 'Português', flag: '🇧🇷' },
    { code: 'it', label: 'Italiano', flag: '🇮🇹' },
    { code: 'nl', label: 'Nederlands', flag: '🇳🇱' }
  ];

  const isActivePath = (path) => {
    const currentPath = location?.pathname;
    // Remove language prefix for comparison
    const normalizedCurrentPath = currentPath?.replace(/^\/(es|pt|fr|de|it|nl)/, '') || '/';
    const normalizedPath = path === '/' ? '/' : path;
    return normalizedCurrentPath === normalizedPath;
  };

  const handleLanguageChange = (langCode) => {
    const newPath = switchLanguage(location?.pathname, langCode);
    
    // Save to localStorage for persistence
    try {
      localStorage.setItem('selectedLanguage', langCode);
      localStorage.setItem('preferredLanguage', langCode);
    } catch (e) {
      // Handle localStorage errors silently
    }
    
    // Navigate to new path
    navigate(newPath);
    setIsLanguageDropdownOpen(false);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const currentLangData = languages?.find(lang => lang?.code === currentLanguage);

  // Get navigation path with current language prefix
  const getLocalizedPath = (path) => {
    if (currentLanguage === 'en') {
      return path;
    }
    return `/${currentLanguage}${path}`;
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-100 bg-card shadow-elevation-2">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center gap-8">
            <Link 
              to={currentLanguage === 'en' ? '/' : `/${currentLanguage}`}
              className="flex items-center gap-3 transition-smooth hover:opacity-80"
              onClick={closeMobileMenu}
            >
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                <Icon name="Calculator" size={24} color="var(--color-primary)" />
              </div>
              <span className="text-xl font-heading font-semibold text-foreground hidden sm:block">
                FinancialCalculatorNetwork
              </span>
            </Link>

            <nav className="hidden md:flex items-center gap-2">
              {navigationItems?.map((item) => (
                <Link
                  key={item?.path}
                  to={getLocalizedPath(item?.path)}
                  className={`
                    flex items-center gap-2 px-4 py-2 rounded-md
                    transition-smooth hover-lift active-press
                    ${isActivePath(item?.path)
                      ? 'bg-primary text-primary-foreground'
                      : 'text-foreground hover:bg-muted'
                    }
                  `}
                >
                  <Icon name={item?.icon} size={18} />
                  <span className="font-medium text-sm">{item?.label}</span>
                </Link>
              ))}
            </nav>
          </div>

          <div className="flex items-center gap-4">
            {!isCalculatorPage() && (
              <div className="relative">
                <button
                  onClick={() => setIsLanguageDropdownOpen(!isLanguageDropdownOpen)}
                  className="language-select flex items-center gap-2 px-3 py-2 rounded-lg bg-muted hover:bg-muted/80 hover:border-gray-600 transition-all duration-200 ease-in-out cursor-pointer min-h-[44px] border border-transparent"
                  aria-label="Select Language"
                  style={{ fontSize: '16px' }}
                >
                  <span className="text-lg">{currentLangData?.flag}</span>
                  <span className="hidden sm:inline text-sm font-medium text-foreground">
                    {currentLangData?.label}
                  </span>
                  <Icon 
                    name={isLanguageDropdownOpen ? "ChevronUp" : "ChevronDown"} 
                    size={16} 
                    color="var(--color-foreground)"
                  />
                </button>

                {isLanguageDropdownOpen && (
                  <>
                    <div 
                      className="fixed inset-0 z-40" 
                      onClick={() => setIsLanguageDropdownOpen(false)}
                    />
                    <div className="absolute right-0 top-full mt-2 w-48 bg-popover rounded-md shadow-elevation-4 z-50 overflow-hidden">
                      {languages?.map((lang) => (
                        <button
                          key={lang?.code}
                          onClick={() => handleLanguageChange(lang?.code)}
                          className={`
                            w-full flex items-center gap-3 px-4 py-3 text-left
                            transition-smooth hover:bg-muted cursor-pointer
                            ${currentLanguage === lang?.code ? 'bg-muted' : ''}
                          `}
                          style={{ minHeight: '44px' }}
                        >
                          <span className="text-lg">{lang?.flag}</span>
                          <span className="text-sm font-medium text-popover-foreground">
                            {lang?.label}
                          </span>
                          {currentLanguage === lang?.code && (
                            <Icon name="Check" size={16} color="var(--color-primary)" className="ml-auto" />
                          )}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}

            <button
              onClick={toggleMobileMenu}
              className="md:hidden p-2 rounded-md hover:bg-muted transition-smooth active-press"
              aria-label="Toggle mobile menu"
            >
              <Icon 
                name={isMobileMenuOpen ? "X" : "Menu"} 
                size={24} 
                color="var(--color-foreground)"
              />
            </button>
          </div>
        </div>
      </div>
      {isMobileMenuOpen && (
        <>
          <div 
            className="fixed inset-0 bg-background z-40 md:hidden"
            onClick={closeMobileMenu}
          />
          <nav className="fixed top-16 left-0 right-0 bg-card shadow-elevation-4 z-50 md:hidden">
            <div className="px-6 py-4 space-y-2">
              {navigationItems?.map((item) => (
                <Link
                  key={item?.path}
                  to={getLocalizedPath(item?.path)}
                  onClick={closeMobileMenu}
                  className={`
                    flex items-center gap-3 px-4 py-3 rounded-md
                    transition-smooth active-press
                    ${isActivePath(item?.path)
                      ? 'bg-primary text-primary-foreground'
                      : 'text-foreground hover:bg-muted'
                    }
                  `}
                >
                  <Icon name={item?.icon} size={20} />
                  <span className="font-medium">{item?.label}</span>
                </Link>
              ))}
            </div>
          </nav>
        </>
      )}
    </header>
  );
};

export default Header;